import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const StoreContext = createContext(null);

export const USD_RATE = 75;
export const SHIPPING_INR = 0;   // FREE shipping across India
export const SHIPPING_USD = 40;  // international delivery ($40 = ₹3,000)
export const MAX_ITEMS = 10;

// IP-based country detection — responds to VPN changes automatically.
// (Browser GPS is intentionally NOT used: a VPN cannot change GPS, so IP is
// the only signal that matches the visitor's actual network location.)
async function detectCountry() {
  const providers = [
    async () => {
      const r = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3500) });
      const j = await r.json();
      return j.country_code;
    },
    async () => {
      const r = await fetch("https://ipwho.is/", { signal: AbortSignal.timeout(3500) });
      const j = await r.json();
      if (j.success === false) throw new Error("ipwho.is failed");
      return j.country_code;
    },
  ];
  for (const p of providers) {
    try {
      const code = await p();
      if (code) return code === "IN" ? "IN" : "INTL";
    } catch { /* try next provider */ }
  }
  return "IN";
}

export function StoreProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState(null);
  const [showcase, setShowcase] = useState({});
  const [config, setConfig] = useState({ payments_mode: "demo", max_items: MAX_ITEMS });
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("rl_cart") || "[]"); } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [currency, setCurrency] = useState(() => localStorage.getItem("rl_geo_currency") || null);

  useEffect(() => {
    const load = () => {
      api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
      api.get("/content").then((r) => setContent(r.data)).catch(() => {});
      api.get("/showcase").then((r) => setShowcase(r.data)).catch(() => {});
      api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    };
    load();
    // live refresh: admin changes appear without manual reload
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const t = setInterval(load, 20000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      clearInterval(t);
    };
  }, []);

  // fully automatic currency detection — re-checked by IP on every visit,
  // so turning a VPN on/off updates prices on the next page load.
  // The cached value is only a placeholder to avoid a flicker while detecting.
  useEffect(() => {
    detectCountry().then((c) => {
      const cur = c === "IN" ? "INR" : "USD";
      setCurrency(cur);
      localStorage.setItem("rl_geo_currency", cur);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem("rl_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((product) => {
    let added = true;
    setCart((prev) => {
      const found = prev.find((i) => i.product.id === product.id);
      if (found) {
        return prev.map((i) => i.product.id === product.id ? { ...i, quantity: Math.min(i.quantity + 1, 20) } : i);
      }
      if (prev.length >= MAX_ITEMS) { added = false; return prev; }
      return [...prev, { product, quantity: 1 }];
    });
    return added;
  }, []);

  const updateQty = useCallback((pid, qty) => {
    setCart((prev) => qty <= 0
      ? prev.filter((i) => i.product.id !== pid)
      : prev.map((i) => i.product.id === pid ? { ...i, quantity: Math.min(qty, 20) } : i));
  }, []);

  const removeItem = useCallback((pid) => {
    setCart((prev) => prev.filter((i) => i.product.id !== pid));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  // selling price of a product in the visitor's currency
  const priceOf = useCallback((p) => {
    if (currency === "USD") return p.price_usd || Math.round(p.price_inr / USD_RATE);
    return Math.round(p.price_inr);
  }, [currency]);

  // before-discount price (null when no discount)
  const originalOf = useCallback((p) => {
    let orig = null;
    if (currency === "USD") {
      orig = p.original_price_usd || (p.original_price_inr ? Math.round(p.original_price_inr / USD_RATE) : null);
    } else {
      orig = p.original_price_inr ? Math.round(p.original_price_inr) : null;
    }
    return orig && orig > priceOf(p) ? orig : null;
  }, [currency, priceOf]);

  // format a number that is ALREADY in the visitor's currency
  const money = useCallback((v) => {
    return currency === "USD" ? `$${Number(v).toLocaleString("en-US")}` : `₹${Number(v).toLocaleString("en-IN")}`;
  }, [currency]);

  // legacy helper: convert an INR amount then format
  const fmt = useCallback((inr) => money(currency === "USD" ? Math.round(inr / USD_RATE) : Math.round(inr)), [currency, money]);

  const totals = useMemo(() => {
    const subtotal = cart.reduce((s, i) => {
      const unit = currency === "USD" ? (i.product.price_usd || Math.round(i.product.price_inr / USD_RATE)) : i.product.price_inr;
      return s + unit * i.quantity;
    }, 0);
    // order shipping = highest per-product charge (blank on a product = default ₹0 India / $40 intl)
    const shipping = cart.length === 0 ? 0 : cart.reduce((mx, i) => {
      const s = currency === "USD"
        ? (i.product.shipping_usd ?? SHIPPING_USD)
        : (i.product.shipping_inr ?? SHIPPING_INR);
      return Math.max(mx, s);
    }, 0);
    return { subtotal, shipping, total: subtotal + shipping };
  }, [cart, currency]);

  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.quantity, 0), [cart]);

  // banner slider state shared with navbar (dark flag drives nav text colour)
  const banners = useMemo(() => {
    const d1 = content?.hero_image || "/assets/banners/diwali-prices.jpg";
    const d2 = content?.hero_image_2 || "/assets/banners/anniversary-25.jpg";
    const desktop = [
      { src: d1, dark: d1.includes("anniversary"), alt: "Exclusive Diwali Prices — Rudralife" },
      { src: d2, dark: d2.includes("anniversary"), alt: "Rudralife 25th Anniversary" },
    ];
    const m1 = content?.hero_image_mobile || d1;
    const m2 = content?.hero_image_mobile_2 || d2;
    const mobile = [
      { src: m1, dark: m1.includes("anniversary"), alt: "Exclusive Diwali Prices — Rudralife" },
      { src: m2, dark: m2.includes("anniversary"), alt: "Rudralife 25th Anniversary" },
    ];
    return { desktop, mobile };
  }, [content]);

  return (
    <StoreContext.Provider value={{
      products, setProducts, content, setContent, showcase, config,
      cart, addToCart, updateQty, removeItem, clearCart,
      cartOpen, setCartOpen, cartCount, banners, bannerIdx, setBannerIdx,
      currency: currency || "INR", priceOf, originalOf, money, fmt, totals,
    }}>
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
